import { collection, doc, getDoc, onSnapshot, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  COL, type ArtistApplication, type Artwork, type Designer, type DesignerEarning,
} from '@eprintsmart/shared';
import { db } from '../firebase';

/** Artists, their work, and the rights complaints against it. */

const fns = () => getFunctions(undefined, 'asia-south1');

export interface ArtworkReport {
  id: string;
  artworkId: string;
  designerId: string;
  reason: string;
  detail: string;
  reporterName: string;
  reporterEmail: string;
  status: 'OPEN' | 'RESOLVED';
  resolution?: string;
  createdAt: number;
}

export function watchDesigners(cb: (rows: Designer[]) => void): () => void {
  return onSnapshot(query(collection(db, COL.designers)), (snap) =>
    cb(snap.docs.map((d) => ({ ...(d.data() as Designer), id: d.id }))),
  );
}

/**
 * Open complaints, newest first.
 *
 * Only OPEN ones: this is a work queue, not a history. A resolved report is
 * still on the artwork document if somebody needs the trail.
 */
export function watchOpenReports(cb: (rows: ArtworkReport[]) => void): () => void {
  return onSnapshot(
    query(collection(db, COL.artworkReports), where('status', '==', 'OPEN')),
    (snap) => cb(
      snap.docs
        .map((d) => ({ ...(d.data() as ArtworkReport), id: d.id }))
        .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0)),
    ),
  );
}

/** Everything currently suspended, which is what a moderator actually works
 *  through — a report suspends immediately, so this is the real queue. */
export function watchSuspendedArtwork(cb: (rows: Artwork[]) => void): () => void {
  return onSnapshot(
    query(collection(db, COL.artworks), where('status', '==', 'SUSPENDED')),
    (snap) => cb(snap.docs.map((d) => ({ ...(d.data() as Artwork), id: d.id }))),
  );
}

export async function getArtwork(id: string): Promise<Artwork | null> {
  const snap = await getDoc(doc(db, COL.artworks, id));
  return snap.exists() ? ({ ...(snap.data() as Artwork), id: snap.id }) : null;
}

/**
 * Applications waiting on a decision.
 *
 * PENDING only — this is a work queue, not a history. A decided application
 * keeps its record and its reason for the audit trail, but it does not belong
 * in front of somebody deciding today.
 */
export function watchPendingApplications(
  cb: (rows: ArtistApplication[]) => void,
): () => void {
  return onSnapshot(
    query(collection(db, COL.artistApplications), where('status', '==', 'PENDING')),
    (snap) => cb(
      snap.docs
        .map((d) => ({ ...(d.data() as ArtistApplication), id: d.id }))
        .sort((a, b) => Number(a.createdAt) - Number(b.createdAt)),
    ),
  );
}

/** Approving mints the designer, publishes their three pieces and emails them;
 *  rejecting deletes the files and tells them why. Both are one-way. */
export async function reviewArtistApplication(payload: {
  applicationId: string;
  action: 'APPROVE' | 'REJECT';
  reason?: string;
}): Promise<void> {
  await httpsCallable(fns(), 'reviewArtistApplication')(payload);
}

export async function getDesigner(id: string): Promise<Designer | null> {
  const snap = await getDoc(doc(db, COL.designers, id));
  return snap.exists() ? ({ ...(snap.data() as Designer), id: snap.id }) : null;
}

/**
 * One artist's whole body of work, whatever state it is in.
 *
 * Unfiltered on purpose, unlike the storefront's PUBLISHED-only query: the
 * point of looking at an artist in admin is usually that something is wrong,
 * and a page that hides the suspended and removed pieces hides the reason you
 * came.
 */
export function watchArtworkByDesigner(
  designerId: string,
  cb: (rows: Artwork[]) => void,
): () => void {
  return onSnapshot(
    query(collection(db, COL.artworks), where('designerId', '==', designerId)),
    (snap) => cb(
      snap.docs
        .map((d) => ({ ...(d.data() as Artwork), id: d.id }))
        .sort((a, b) => Number(b.createdAt) - Number(a.createdAt)),
    ),
  );
}

/**
 * What this artist has earned, newest first.
 *
 * Sorted client-side rather than with orderBy so the query stays a single
 * equality and needs no composite index — this is one artist's rows, not a
 * collection scan.
 */
export function watchEarningsByDesigner(
  designerId: string,
  cb: (rows: DesignerEarning[]) => void,
): () => void {
  return onSnapshot(
    query(collection(db, COL.designerEarnings), where('designerId', '==', designerId)),
    (snap) => cb(
      snap.docs
        .map((d) => ({ ...(d.data() as DesignerEarning), id: d.id }))
        .sort((a, b) => Number(b.soldAt) - Number(a.soldAt)),
    ),
  );
}

export async function moderateArtwork(payload: {
  artworkId: string;
  action: 'RESTORE' | 'REMOVE';
  note?: string;
}): Promise<void> {
  await httpsCallable(fns(), 'moderateArtwork')(payload);
}

export async function createDesigner(payload: {
  email: string;
  displayName: string;
  handle: string;
}): Promise<void> {
  await httpsCallable(fns(), 'createDesigner')(payload);
}

export async function setDesignerStatus(payload: {
  designerId: string;
  status?: 'ACTIVE' | 'SUSPENDED';
  featured?: boolean;
}): Promise<void> {
  await httpsCallable(fns(), 'setDesignerStatus')(payload);
}

/* ------------------------------------------------------------------ *
 * Settlements
 * ------------------------------------------------------------------ */

export interface SettlementLine {
  id: string;
  orderNumber: string;
  productName: string;
  artworkTitle: string;
  qty: number;
  royaltyMinor: number;
  soldAt: number;
}

export interface SettlementView {
  designerId: string;
  period: string;
  earningCount: number;
  grossMinor: number;
  lines: SettlementLine[];
  /** Sold but not yet delivered + 10 days - paid in a later month. */
  heldCount?: number;
  heldMinor?: number;
  /** Present once approved. The frozen figure, which is what was actually
   *  agreed — shown in preference to the live total. */
  settlement: {
    id: string;
    status: 'DRAFT' | 'APPROVED' | 'PAID';
    grossMinor: number;
    deductionsMinor: number;
    netPayableMinor: number;
    note?: string;
    paidAt?: number;
  } | null;
}

export async function getDesignerSettlement(payload: {
  designerId: string;
  period?: string;
}): Promise<SettlementView> {
  const res = await httpsCallable<typeof payload, SettlementView>(
    fns(), 'getDesignerSettlement',
  )(payload);
  return res.data;
}

export async function approveDesignerSettlement(payload: {
  designerId: string;
  period: string;
  deductionsMinor: number;
  note?: string;
}): Promise<void> {
  await httpsCallable(fns(), 'approveDesignerSettlement')(payload);
}

export async function markDesignerSettlementPaid(payload: {
  designerId: string;
  period: string;
}): Promise<void> {
  await httpsCallable(fns(), 'markDesignerSettlementPaid')(payload);
}
